import { FC, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { NumericTextBox, NumericTextBoxChangeEvent } from '@progress/kendo-react-inputs';
import { DEFAULT_QTY_UNIT, KitItem, PerfectOrderItem, QTY_UNIT_OPTIONS, QtyUnit } from 'src/types/perfectOrder';
import { isSpecialItem } from 'src/utils/perfectOrderRules';
import InformationCircle from 'src/icons/InformationCircle';
import PerfectOrderItemPicker, { AddedPerfectOrderItem } from 'src/components/order/PerfectOrderItemPicker';
import PerfectOrderKitItemsButton from 'src/components/order/PerfectOrderKitItemsButton';
import orderApi from 'src/api/OrderApi';
import { Logger } from 'src/utils/logger';

type EditablePerfectOrderItemField = 'quantity' | 'customerPrice' | 'quantityUnit';

const isAddedItem = (item: PerfectOrderItem): boolean => (item.perfectOrderLineId ?? 0) < 0 || item.retOrderLine == null;
const isUnsavedAddedItem = (item: PerfectOrderItem): boolean => (item.perfectOrderLineId ?? 0) < 0;
const isKitComponent = (item: PerfectOrderItem): boolean => !!item.kitParentItemNumber;

const toNumericTextBoxValue = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

interface Props {
  items: PerfectOrderItem[];
  onItemChange: (item: PerfectOrderItem, field: EditablePerfectOrderItemField, value: number | string | null) => void;
  onToggleItemIncluded: (item: PerfectOrderItem) => void;
  onAddItem: (item: AddedPerfectOrderItem) => void;
  onAddKitItems: (parentItem: PerfectOrderItem, items: AddedPerfectOrderItem[]) => void;
  onRemoveAddedItem: (item: PerfectOrderItem) => void;
  customerNumber?: string;
  warehouseCode?: string;
  shipToNumber?: string;
  disabled?: boolean;
}

const PerfectOrderItemGrid: FC<Props> = ({
  items,
  onItemChange,
  onToggleItemIncluded,
  onAddItem,
  onAddKitItems,
  onRemoveAddedItem,
  customerNumber,
  warehouseCode,
  shipToNumber,
  disabled = false,
}) => {
  const [kitItems, setKitItems] = useState<Record<string, KitItem>>({});
  const sortedItems = useMemo(() => {
    const parentIndexes = new Map<string, number>();
    items.forEach((item, index) => {
      if (item.itemNumber && !isKitComponent(item)) {
        parentIndexes.set(item.itemNumber.toLowerCase(), index);
      }
    });

    return [...(items || [])].sort((a, b) => {
      const aParentIndex = a.kitParentItemNumber ? parentIndexes.get(a.kitParentItemNumber.toLowerCase()) : undefined;
      const bParentIndex = b.kitParentItemNumber ? parentIndexes.get(b.kitParentItemNumber.toLowerCase()) : undefined;
      const aGroupIndex = aParentIndex ?? parentIndexes.get(a.itemNumber?.toLowerCase() ?? '') ?? Number.MAX_SAFE_INTEGER;
      const bGroupIndex = bParentIndex ?? parentIndexes.get(b.itemNumber?.toLowerCase() ?? '') ?? Number.MAX_SAFE_INTEGER;

      if (aGroupIndex !== bGroupIndex) return aGroupIndex - bGroupIndex;
      if (isKitComponent(a) !== isKitComponent(b)) return isKitComponent(a) ? 1 : -1;

      const aIsAdded = isAddedItem(a);
      const bIsAdded = isAddedItem(b);
      if (aIsAdded !== bIsAdded) return aIsAdded ? 1 : -1;
      if (aIsAdded && bIsAdded) return (b.perfectOrderLineId ?? 0) - (a.perfectOrderLineId ?? 0);
      return (a.perfectOrderLineId ?? 0) - (b.perfectOrderLineId ?? 0);
    });
  }, [items]);
  const existingItemNumbers = useMemo(() => sortedItems.map((item) => item.itemNumber ?? '').filter((n) => n.length > 0), [sortedItems]);
  const pendingDeleteCount = useMemo(() => sortedItems.filter((item) => item.pendingDelete === true).length, [sortedItems]);

  useEffect(() => {
    const itemNumbers = Array.from(new Set(sortedItems.map((item) => item.itemNumber).filter((itemNumber): itemNumber is string => !!itemNumber)));
    if (!warehouseCode || itemNumbers.length === 0) {
      setKitItems({});
      return;
    }

    let active = true;
    orderApi
      .getPerfectOrderKitItems(itemNumbers, warehouseCode)
      .then((kits) => {
        if (active) {
          setKitItems(Object.fromEntries(kits.map((kit) => [kit.kitItemNumber.toLowerCase(), kit])));
        }
      })
      .catch((error) => {
        Logger.error('PerfectOrderItemGrid: kit item lookup failed', error);
        if (active) setKitItems({});
      });

    return () => {
      active = false;
    };
  }, [sortedItems, warehouseCode]);

  const handleNumberChange = (item: PerfectOrderItem, field: 'quantity' | 'customerPrice') => (event: NumericTextBoxChangeEvent) => {
    onItemChange(item, field, field === 'customerPrice' ? event.value : (event.value ?? 0));
  };

  const handleQtyUnitChange = (item: PerfectOrderItem) => (event: SelectChangeEvent<QtyUnit>) => {
    onItemChange(item, 'quantityUnit', event.target.value);
  };

  return (
    <Box sx={{ mt: 1 }} width="100%">
      <h4 className="mb-1 mt-0">Order Items</h4>

      <TableContainer component={Paper} sx={{ maxHeight: 360 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell align="center">Include</TableCell>
              <TableCell align="center" padding="none">
                Line
              </TableCell>
              <TableCell align="center" width={100}></TableCell>
              <TableCell align="center" width={130}>
                Vendor #
              </TableCell>
              <TableCell align="left">Item #</TableCell>
              <TableCell align="left">Descriptions</TableCell>
              <TableCell align="left" width={100}>
                UPC
              </TableCell>
              <TableCell align="left" width={100}>
                Qty
              </TableCell>
              <TableCell align="left" width={150}>
                Invoiced Price
              </TableCell>{' '}
              <TableCell align="left" width={150}>
                Override Qty
              </TableCell>
              <TableCell align="left" width={100}>
                Qty Unit
              </TableCell>
              <TableCell align="left" width={150}>
                Override Price
              </TableCell>
              <TableCell align="center">Kit Items</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedItems.map((item) => {
              const special = isSpecialItem(item);
              const pendingDelete = item.pendingDelete === true;
              const added = isAddedItem(item);
              const kitComponent = isKitComponent(item);
              const unsavedAdded = isUnsavedAddedItem(item);
              const rowDisabled = disabled || pendingDelete;
              const kit = item.itemNumber ? kitItems[item.itemNumber.toLowerCase()] : undefined;
              const handleIncludeChange = () => {
                if (unsavedAdded && !pendingDelete) {
                  onRemoveAddedItem(item);
                } else {
                  onToggleItemIncluded(item);
                }
              };
              return (
                <TableRow
                  key={item.perfectOrderLineId ?? `${item.retOrderLine ?? 'added'}-${item.itemNumber ?? 'item'}`}
                  hover={!pendingDelete}
                  className={!pendingDelete && kitComponent ? 'kit-component-row' : undefined}
                  sx={{
                    opacity: pendingDelete ? 0.45 : 1,
                    backgroundColor: pendingDelete ? 'action.disabledBackground' : undefined,
                    '& td': { textDecoration: pendingDelete ? 'line-through' : 'none' },
                  }}
                >
                  <TableCell align="center" sx={{ textDecoration: 'none !important' }}>
                    <Checkbox
                      checked={!pendingDelete}
                      onChange={handleIncludeChange}
                      disabled={disabled}
                      title={pendingDelete ? 'Restore item' : added ? 'Remove added item' : 'Remove item'}
                    />
                  </TableCell>
                  <TableCell align="center">{item.retOrderLine ?? <Box sx={{ color: 'text.disabled' }}>—</Box>}</TableCell>
                  <TableCell align="center">
                    {kitComponent ? <Chip size="small" label="Kit component" /> : added ? <Chip size="small" label="Added" color="info" variant="outlined" /> : null}
                  </TableCell>
                  <TableCell align="center">{item.vendorNumber}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box>{item.itemNumber}</Box>
                    </Box>
                  </TableCell>
                  <TableCell align="left" width={200}>
                    <Tooltip title={item.description}>
                      <Box sx={{ pl: kitComponent ? 1.5 : 0 }}>
                        <div className={'ellipsis-cell'}>{item.description}</div>
                        {kitComponent && (
                          <Typography variant="caption" color="text.secondary">
                            Component of kit {item.kitParentItemNumber}
                          </Typography>
                        )}
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="left" width={100}>
                    <Tooltip title={item.upc}>
                      <Box sx={{ pl: kitComponent ? 1.5 : 0 }}>
                        <div className={'ellipsis-cell'}>{item.upc}</div>
                      </Box>
                    </Tooltip>
                  </TableCell>

                  <TableCell>
                    {added ? (
                      <Box sx={{ color: 'text.disabled', textAlign: 'center' }}>—</Box>
                    ) : (
                      <NumericTextBox value={item.originalQuantity ?? 0} min={0} spinners={false} format="n0" width={45} disabled />
                    )}
                  </TableCell>
                  <TableCell>
                    {added ? (
                      <Box sx={{ color: 'text.disabled', textAlign: 'center' }}>—</Box>
                    ) : (
                      <NumericTextBox value={toNumericTextBoxValue(item.originalCustomerPrice)} min={0} spinners={false} format="c2" width={120} disabled />
                    )}
                  </TableCell>
                  <TableCell>
                    <NumericTextBox
                      value={item.quantity ?? 0}
                      defaultValue={item.originalQuantity ?? 0}
                      onChange={handleNumberChange(item, 'quantity')}
                      min={0}
                      spinners={true}
                      format="n0"
                      width={75}
                      disabled={rowDisabled}
                    />
                  </TableCell>
                  <TableCell>
                    <Select<QtyUnit>
                      value={item.quantityUnit ?? DEFAULT_QTY_UNIT}
                      onChange={handleQtyUnitChange(item)}
                      size="small"
                      disabled={rowDisabled}
                      sx={{ '& .MuiSelect-select': { py: 0.5 } }}
                    >
                      {QTY_UNIT_OPTIONS.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell>
                    <NumericTextBox
                      value={toNumericTextBoxValue(item.customerPrice)}
                      defaultValue={toNumericTextBoxValue(item.originalCustomerPrice)}
                      onChange={handleNumberChange(item, 'customerPrice')}
                      min={0}
                      spinners={false}
                      format="c2"
                      width={120}
                      disabled={rowDisabled}
                    />
                  </TableCell>
                  <TableCell align="center" width={170}>
                    {kit && !pendingDelete && (
                      <PerfectOrderKitItemsButton
                        kit={kit}
                        kitQuantity={item.originalQuantity ?? item.quantity}
                        existingItemNumbers={existingItemNumbers}
                        disabled={disabled}
                        onAddItems={(components) => onAddKitItems(item, components)}
                      />
                    )}
                  </TableCell>

                  <TableCell>
                    {special && (
                      <Tooltip title="Special Order Item — Requires Return to Warehouse">
                        <Box component="span" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'warning.main' }}>
                          <InformationCircle fontSize="small" />
                        </Box>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {sortedItems.length === 0 && (
              <TableRow>
                <TableCell colSpan={13} align="center">
                  No items found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <PerfectOrderItemPicker
        customerNumber={customerNumber}
        warehouseCode={warehouseCode}
        shipToNumber={shipToNumber}
        existingItemNumbers={existingItemNumbers}
        disabled={disabled}
        onAdd={onAddItem}
      />
      {pendingDeleteCount > 0 && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {pendingDeleteCount} {pendingDeleteCount === 1 ? 'item' : 'items'} will be deleted once saved.
        </Alert>
      )}
    </Box>
  );
};

export default PerfectOrderItemGrid;
